import { COURSE_ACCESSIBILITY_GUIDE } from '@/common/constants/external-urls';
import { usePosthogTracking } from '@/hooks/usePosthogTracking';
import { AppButton } from '@/ui/buttons';
import { A11yIcon } from '@/utils/helpers';
import { PostHogCustomEvent } from '@/utils/posthog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const anchorStyles = `
  position: absolute;
  top: var(--medium-padding);
  right: var(--medium-padding);
  z-index: 10;
`;

const StyledIconButton = styled.button`
  ${anchorStyles}
  width: var(--default-input-height);
  height: var(--default-input-height);
  border-radius: var(--xsmall-border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--gray-dark);
  background-color: var(--white-main);
  cursor: pointer;
  padding: 0;

  &:focus-visible {
    outline: 2px solid var(--purple-text);
    outline-offset: var(--xxxsmall-padding);
  }
`;

const StyledPanel = styled.div`
  ${anchorStyles}
  width: 658px;
  max-width: calc(100% - var(--medium-padding) * 2);
  background-color: var(--white-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding-small);
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
`;

const StyledPanelText = styled.p`
  flex: 1;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: 0;
  min-width: 0;
  color: var(--black-main);
`;

const StyledCtaButton = styled(AppButton)`
  &.p-button {
    background-color: var(--purple-deepest) !important;
    border-color: var(--purple-deepest) !important;
    border-radius: var(--default-border-radius) !important;
    white-space: nowrap;
  }

  &.p-button:hover {
    background-color: var(--purple-highlight) !important;
    border-color: var(--purple-highlight) !important;
  }
`;

const StyledCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  color: var(--gray-darker);

  i {
    font-size: var(--xsmall-font-size);
  }

  &:focus-visible {
    outline: 2px solid var(--purple-text);
    outline-offset: var(--xxxsmall-padding);
  }
`;

export const AccessibilityIcon = () => {
  const { t } = useTranslation();
  const { trackPostHogEvent } = usePosthogTracking();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    trackPostHogEvent(PostHogCustomEvent.ACCESSIBILITY_ICON_CLICKED);
    setIsOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  if (isOpen) {
    return (
      <StyledPanel
        role="region"
        aria-label={t('content.accessibility.panel.label')}
      >
        <StyledPanelText
          dangerouslySetInnerHTML={{
            __html: t('content.accessibility.readAloud.description'),
          }}
        />
        <StyledCtaButton
          label={t('content.accessibility.readAloud.cta')}
          onClick={() =>
            window.open(
              COURSE_ACCESSIBILITY_GUIDE,
              '_blank',
              'noopener,noreferrer',
            )
          }
        />
        <StyledCloseButton
          aria-label={t('generic.close')}
          onClick={() => setIsOpen(false)}
        >
          <i className="pi pi-times" />
        </StyledCloseButton>
      </StyledPanel>
    );
  }

  return (
    <StyledIconButton
      aria-label={t('content.accessibility.icon.label')}
      aria-expanded={false}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
    >
      <A11yIcon />
    </StyledIconButton>
  );
};
